import { useEffect, useMemo, useState } from 'react';
import { useSkinSession } from '../../salon/context/SkinSessionContext';
import { SESSION_APP_DATA_BASE } from '../../salon/config/sessionAppData';
import { fetchWithTimeout } from '../../salon/utils/fetchWithTimeout';
import { getSalonAdminApiBaseUrl } from '../../salon-admin/utils/apiConfig';
import { resolveFaceOrganizationId } from '../utils/faceIdentityApi';
import { kioskConfigAuthHeaders } from '../../shared/kioskConfigAuth';
import { KIOSK_PAGE_KEYS, defaultConfigForKey } from '../utils/kioskLandingDefaults';
import { getRegisterFormConfig } from '../utils/registerFormHelpers';

function orgQuery() {
  const org = resolveFaceOrganizationId();
  return org ? `organization_id=${encodeURIComponent(org)}` : '';
}

async function fetchLandingConfig(pageKey, token) {
  const base = (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
  const q = orgQuery();
  if (base) {
    try {
      const url = `${base}/kiosk-landings/${encodeURIComponent(pageKey)}/public?_=${Date.now()}${q ? `&${q}` : ''}`;
      const res = await fetch(url, {
        cache: 'no-store',
        credentials: 'include',
        headers: kioskConfigAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.config) return data.config;
    } catch (_) {
      /* fall through */
    }
  }
  if (token) {
    const url = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/kiosk-landing/${encodeURIComponent(pageKey)}?_=${Date.now()}${q ? `&${q}` : ''}`;
    const res = await fetchWithTimeout(url, {
      timeoutMs: 15000,
      cache: 'no-store',
      credentials: 'include',
      headers: kioskConfigAuthHeaders(),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data?.config) return data.config;
  }
  return null;
}

/**
 * Shared register form for Face Identity + Salon (canonical: face_identity config).
 * Loads via session token when available, else public salon-admin landing endpoint.
 */
export function useKioskRegisterForm() {
  const { token, tokenReady } = useSkinSession();
  const [config, setConfig] = useState(() => defaultConfigForKey(KIOSK_PAGE_KEYS.FACE_IDENTITY));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        // Prefer session when ready; public admin endpoint always works as fallback.
        const sessionToken = tokenReady ? token : null;
        const next = await fetchLandingConfig(KIOSK_PAGE_KEYS.FACE_IDENTITY, sessionToken);
        if (!cancelled && next) setConfig(next);
      } catch (_) {
        /* keep defaults */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token, tokenReady]);

  const registerForm = useMemo(() => getRegisterFormConfig(config), [config]);

  return { registerForm, loading, token, config };
}
